import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  MuiThemeProvider,
  Tooltip,
  Typography,
  withStyles,
  IconButton,
  OutlinedInput,
} from '@material-ui/core';
import { orderBy, cloneDeep, clone, without, isNil, isEmpty, groupBy, omit } from 'lodash';
import TemplateModalWithButton from 'src/components/TemplateModalWithButton/TemplateModalWithButton';
import viewConfiguratorStyle, {
  getItemStyle,
  getListStyle,
  configuratorAccordionOverrides,
  configuratorAccordionSummaryOverrides,
  configuratorAccordionDetailsOverrides,
  configuratorAccordionTypographyOverrides,
  resetButtonStyles,
} from './ViewConfiguratorModal.styles';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import SubheaderQuickActionButton from '../Subheader/SubheaderQuickActionButton';
import { muiTheme } from 'src/utils/Style/Theme';
import { ExpandMore, Close } from '@material-ui/icons';
import ServiceContainer from 'src/ServiceContainer';
import { findDuplicateDataIndices, flattenConfigItems } from 'src/utils/Pivot/Flatten';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { ARRAY_EDITOR_COLUMN_ID_PREFIX } from 'src/utils/Domain/Constants';
import {
  applyGroupingInPlace,
  applyVisibilityToFilteredItems,
  getConfigKey,
  getFilteredLeafItems,
  getFilteredViewConfig,
  getParentCheckboxState,
  hasColumnOrderChanged,
  isPinned,
  itemVisible,
  itemVisibleInFilter,
  reorderItems,
} from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.utils';
import type {
  StyledTypographyProps,
  ViewConfiguratorModalProps,
  ViewConfiguratorModalState,
} from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.types';
import { toast } from 'react-toastify';

const StyledAccordion = withStyles(() => configuratorAccordionOverrides)(Accordion);
const StyledAccordionSummary = withStyles(() => configuratorAccordionSummaryOverrides)(AccordionSummary);
const StyledAccordionDetails = withStyles(() => configuratorAccordionDetailsOverrides)(AccordionDetails);
/**
 * Component that intercepts custom props used for styles
 * so they do not get passed down to the DOM element
 * */
const OmitCustomPropsTypography = (props: StyledTypographyProps) => <Typography {...omit(props, 'hasIcon')} />;
const StyledTypography = withStyles(() => configuratorAccordionTypographyOverrides)(OmitCustomPropsTypography);

export default class ViewConfiguratorModal extends React.Component<
  ViewConfiguratorModalProps,
  ViewConfiguratorModalState
> {
  constructor(props: ViewConfiguratorModalProps) {
    super(props);
    this.state = {
      showResetConfirmation: false,
      viewConfig: [],
      onOpenViewConfig: [],
      searchValue: '',
    };
  }

  componentDidMount() {
    this.checkForDuplicateColumns();
  }

  // Fires on mount so this warns for every view on load
  checkForDuplicateColumns = () => {
    const columns = Array.isArray(this.props.viewConfig.columns)
      ? this.props.viewConfig.columns
      : this.props.viewConfig.view;
    const duplicateKeys = findDuplicateDataIndices(columns || []);
    if (duplicateKeys.length === 0) {
      return;
    }
    const duplicateKeysMessage = duplicateKeys.join(', ');
    ServiceContainer.loggingService.warn(
      `Duplicate columns detected in view config by dataIndex: ${duplicateKeysMessage}`,
    );
    ServiceContainer.toast.error(`Duplicate columns detected in view config by dataIndex: ${duplicateKeysMessage}`, {
      type: 'warning',
      toastId: `duplicate-columns-${duplicateKeysMessage}`,
      position: toast.POSITION.TOP_LEFT,
    });
  };

  onSelectAllFiltered = (isVisible: boolean) => {
    const { searchValue, viewConfig } = this.state;
    const filteredViewConfig = getFilteredViewConfig(searchValue, viewConfig);
    this.setState(({ viewConfig }) => ({
      viewConfig: applyVisibilityToFilteredItems(viewConfig, filteredViewConfig, isVisible),
    }));
  };

  onOpen = async (): Promise<void> => {
    const gridApi = this.props.getGridApi?.();

    // this need to check the keyGroupedColumns & current column
    const useGroupedSnapshot =
      gridApi &&
      // !this.props.viewConfig.isDefault &&
      this.props.viewConfig.keyGroupedColumns &&
      !hasColumnOrderChanged(gridApi, this.props.viewConfig.keyGroupedColumns);
    const raw =
      // if we’ve already stored a grouped snapshot & equal to current grid column state then use it
      useGroupedSnapshot
        ? this.props.viewConfig.keyGroupedColumns
        : // else if it’s using columns, fallback to other logic
          Array.isArray(this.props.viewConfig.columns)
          ? this.props.viewConfig.columns
          : this.props.viewConfig.view;
    const viewColumnsConfig: TenantConfigViewItem[] = cloneDeep(raw || []);

    // Reorder the config based off the grid, so if the user makes changes via aggrid, they reflect here
    const reorderConfig: TenantConfigViewItem[] = [];
    if (this.props.getGridApi) {
      const gridApi = this.props.getGridApi();
      if (gridApi) {
        // If a config has nested columns, check if some were hidden in grid and keep them hidden/unhidden
        const agGridColumnsState = gridApi.getColumnState();
        // track what we have already placed (by key) so we don't place the same item twice
        const placedKeys = new Set<string>();
        const keyOf = (c: TenantConfigViewItem) => c.id ?? c.dataIndex ?? '';
        const sameKey = (a: TenantConfigViewItem, b: TenantConfigViewItem) => keyOf(a) && keyOf(a) === keyOf(b);
        const isCfgVisible = (c: TenantConfigViewItem) => c.visible !== false && c.hidden !== true;
        // match AG Grid duplicate colIds (with the _1, _2 suffixes) to config keys
        // suffix must be purely numeric so "dmd_r" does not match "dmd_r_csp", etc.
        // jsut pulled this up from below
        const matchesColId = (stateColId: string, key: string) => {
          if (stateColId === key) return true;
          if (!stateColId.startsWith(key + '_')) return false;
          const suffix = stateColId.slice(key.length + 1);
          return suffix.length > 0 && /^\d+$/.test(suffix);
        };

        // For duplicate-dataIndex groups that include a hideFromConfigurator entry (ex: "Group"),
        // identify which agGrid colIds belong to those hidden columns so they can be excluded
        // when selecting the right slot for each visible duplicate
        // AG-Grid assigns colIds in config definition order: 1st occurrence → exact dataIndex,
        // 2nd → dataIndex_1, 3rd → dataIndex_2, etc.
        const hideFromConfiguratorColIds: string[] = [];
        const viewColsByDataIndex = groupBy(viewColumnsConfig, (col) => col.dataIndex ?? '');
        Object.entries(viewColsByDataIndex).forEach(([dataIndex, cols]) => {
          if (cols.length <= 1 || !dataIndex) return;
          cols.forEach((col, defIdx) => {
            if (!col.hideFromConfigurator) return;
            hideFromConfiguratorColIds.push(defIdx === 0 ? dataIndex : `${dataIndex}_${defIdx}`);
          });
        });
        // Count non-hideFromConfigurator occurrences per key so each duplicate-dataIndex
        // column gets matched to its own distinct agGrid slot instead of all sharing one
        const keyOccurrenceCount: Record<string, number> = {};

        viewColumnsConfig
          .filter((viewColumnConfig) => !viewColumnConfig.hideFromConfigurator)
          .map((viewColumnConfig, i) => {
            // Determines if the current viewColumnConfig is present in grid columns state
            // in case the user made column changes within the grid before opening modal.
            // Those changes need to be reflected in the configurator modal.
            // Multiple items may be returned if there are columns with the same id/dataIndex value.
            // In the event multiple columns have identiy `dataIndex`s, ag-grid will append `_1`, `_2` etc.
            // to the duplicates.  Because of that, we match with matchesColId() so that e.g. "dmd_r"
            // does not accidentally match "dmd_r_csp".
            const colKey = viewColumnConfig.id ?? viewColumnConfig.dataIndex ?? '';
            // When a column has a separate `id` and `dataIndex`, ag-grid registers it by
            // dataIndex as colId. Fall back to dataIndex matching so the column lands at
            // its correct position rather than being appended to the end of reorderConfig.
            let agGridColumnMatches = agGridColumnsState.filter((gridColumn) =>
              colKey ? matchesColId(gridColumn.colId, colKey) : false,
            );
            if (
              agGridColumnMatches.length === 0 &&
              viewColumnConfig.dataIndex &&
              viewColumnConfig.dataIndex !== colKey
            ) {
              agGridColumnMatches = agGridColumnsState.filter((gridColumn) =>
                matchesColId(gridColumn.colId, viewColumnConfig.dataIndex!),
              );
            }

            // Array editor columns (useArrayEdit / arrayOrderDataIndex) are exploded in ag-grid
            // into one column per size/element with colIds like `array_editor_<dataIndex>_<element>` -
            // those colIds dont match the configs dataIndex directly, so fall back to a prefix search
            if (agGridColumnMatches.length === 0 && viewColumnConfig.arrayOrderDataIndex) {
              const arrayEditorPrefix = `${ARRAY_EDITOR_COLUMN_ID_PREFIX}_${colKey}_`;
              agGridColumnMatches = agGridColumnsState.filter((gridColumn) =>
                gridColumn.colId.startsWith(arrayEditorPrefix),
              );
            }

            // Finds current viewColumnConfig in the viewColumnsConfig.
            // Multiple items may be returned if there are columns with the same id/dataIndex value.
            const viewColumnMatches = viewColumnsConfig.filter(
              (viewColumn) => viewColumn.dataIndex === viewColumnConfig.dataIndex,
            );

            // Some view configs have a group property that shares the same dataIndex as another column in the config.
            // Finds correct config item in viewColumnConfigs that is not supposed to be hidden from the configurator.
            // This is most likely going to filter out the 'Group' column.
            const visibleViewColumnIndex =
              viewColumnMatches && viewColumnMatches.findIndex((a) => !a.hideFromConfigurator);

            // Exclude agGrid slots that belong to hideFromConfigurator columns (ex: "Group"),
            // then use occurrence count so each visible duplicate gets its own distinct slot
            const filteredMatches = agGridColumnMatches.filter((m) => !hideFromConfiguratorColIds.includes(m.colId));
            const occCount = keyOccurrenceCount[colKey] ?? 0;
            keyOccurrenceCount[colKey] = occCount + 1;
            const agGridColumn = filteredMatches[occCount] ?? agGridColumnMatches[visibleViewColumnIndex || 0];

            // Reorder columns based on the grid
            // If the viewConfigItem is found in the gridColumn's state, place it in the order based on grid
            if (agGridColumn) {
              const agGridColumnIndex = agGridColumnsState.indexOf(agGridColumn);
              const existingAtIndex = reorderConfig[agGridColumnIndex];
              // dont overwrite a visible twin with a config-hidden duplicate that shares the same key
              // this happens in the second loop below but we need it here as well to not resurect unwanted hidden duplicates
              if (
                !existingAtIndex || //same column id is hidden at placed index
                (!isCfgVisible(existingAtIndex) &&
                  isCfgVisible(viewColumnConfig) &&
                  sameKey(existingAtIndex, viewColumnConfig))
              ) {
                reorderConfig[agGridColumnIndex] = viewColumnConfig;
                const k = keyOf(viewColumnConfig);
                if (k) placedKeys.add(k);
              }
            }

            // If config item has columns has to be ordered differently
            const nested = viewColumnConfig.columns;

            // Sync nested childrens visibilty through drags like we do for the parent columns below
            nested?.forEach((child) => {
              const childKey = child.id ?? child.dataIndex ?? '';
              const childGridState = agGridColumnsState.find(
                (gridColumn) => childKey && matchesColId(gridColumn.colId, childKey),
              );
              if (childGridState?.hide) {
                child.visible = false;
                child.hidden = true;
              } else if (childGridState && isNil(child.visible)) {
                child.visible = true;
              }
            });

            if (!agGridColumn && nested && nested.length > 0) {
              //check if all child column is not visible
              const nestedColumns = nested.filter((col) => col.visible !== false);
              let nestedAgGridColumn =
                !isEmpty(nestedColumns) &&
                agGridColumnsState.find(
                  (a) => a.colId.replace(/_\d$/, '') == (nestedColumns[0].id || nestedColumns[0].dataIndex),
                );
              // A group whose children are array-editor columns wont match by direct dataIndex
              // (the grid has `array_editor_<dataIndex>_<element>` colIds), so fall back to the
              // same prefix search used for top-level array-editor columns
              if (!nestedAgGridColumn && !isEmpty(nestedColumns) && nestedColumns[0].arrayOrderDataIndex) {
                const childKey = nestedColumns[0].id ?? nestedColumns[0].dataIndex ?? '';
                const arrayEditorPrefix = `${ARRAY_EDITOR_COLUMN_ID_PREFIX}_${childKey}_`;
                nestedAgGridColumn = agGridColumnsState.find((a) => a.colId.startsWith(arrayEditorPrefix)) || false;
              }
              if (nestedAgGridColumn) {
                const nestedAgGridColumnIndex = agGridColumnsState.indexOf(nestedAgGridColumn);
                reorderConfig[nestedAgGridColumnIndex] = viewColumnConfig;
                const k = keyOf(viewColumnConfig);
                if (k) placedKeys.add(k);
              } else if (!isEmpty(nestedColumns)) {
                // Has visible children but no matching ag-grid slot — preserve relative order via i offset
                reorderConfig[i + 99] = viewColumnConfig;
              } else {
                // All children hidden — push to dense end so no sparse gap is created.
                // reorderItems will sort it to the absolute end since visible:false.
                reorderConfig.push(viewColumnConfig);
              }
            }

            // Determine and set column state for configurator,
            // visible determines the checkbox checked state.
            // Checking for hidden and visible in case config is setup with either or (we weren't consistent in config)
            // Grouped columns are synced too - this used to skip them because the live state was thought to flip
            // on group expand/collapse, but ag-grid only changes which children are displayed there, never
            // column.isVisible() (which is what getColumnState().hide reads), so it reflects real hides
            if (agGridColumn && agGridColumn.hide) {
              viewColumnConfig.visible = false;
              viewColumnConfig.hidden = true;
            } else if (agGridColumn && !agGridColumn.hide && isNil(viewColumnConfig.visible)) {
              // Only sync to visible when the config has no explicit visibility decision
              // Pinned columns removed from column defs (visible:false) linger in getColumnState()
              // with stale hide:false — checking isNil prevents that from flipping them back to visible
              // basically if you hide a pinned column that has a duplicate dataindex - it can pop back in on fav save
              viewColumnConfig.visible = true;
            }

            // Now that we get the actual state of the column per config property,
            // we can change things like pinned as well
            if (agGridColumn && !agGridColumn.pinned) {
              viewColumnConfig.pinned = undefined;
            } else if (agGridColumn && agGridColumn.pinned) {
              viewColumnConfig.pinned = 'left';
            }

            return viewColumnConfig;
          });

        // Insert skipped columns (e.g., with hideFromConfigurator) into reorderConfig
        // at their correct index from the current grid state instead of pushing them to the end.
        viewColumnsConfig.forEach((columnConfig) => {
          const key = keyOf(columnConfig);
          if (!key) return;

          // If we already placed this key in the first pass, don't place again
          if (placedKeys.has(key)) return;

          // If a grid-visible twin with the same key is already placed, skip grid-hidden duplicates
          if (
            !isCfgVisible(columnConfig) &&
            reorderConfig.some((c) => c && sameKey(c, columnConfig) && isCfgVisible(c))
          ) {
            return;
          }

          // Pick the best matching AG slot for this key (prefer pinned > visible > first)
          let candidateIdxs = agGridColumnsState
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => matchesColId(s.colId, key))
            .map(({ i }) => i);

          // Same fallback as in the first pass: array editor columns expand into ag-grid colIds
          // like `array_editor_<dataIndex>_<element>` which dont match the config key directly
          if (candidateIdxs.length === 0 && columnConfig.arrayOrderDataIndex) {
            const arrayEditorPrefix = `${ARRAY_EDITOR_COLUMN_ID_PREFIX}_${key}_`;
            candidateIdxs = agGridColumnsState
              .map((s, i) => ({ s, i }))
              .filter(({ s }) => s.colId.startsWith(arrayEditorPrefix))
              .map(({ i }) => i);
          }

          const gridIndex =
            candidateIdxs.find((i) => agGridColumnsState[i].pinned) ??
            candidateIdxs.find((i) => !agGridColumnsState[i].hide) ??
            (candidateIdxs.length ? candidateIdxs[0] : -1);

          if (gridIndex !== -1) {
            const existing = reorderConfig[gridIndex];

            // Dont overwrite a grid-visible twin with a grid-hidden one
            if (existing && isCfgVisible(existing) && !isCfgVisible(columnConfig) && sameKey(existing, columnConfig)) {
              return;
            }

            reorderConfig[gridIndex] = columnConfig;
            placedKeys.add(key);
          } else {
            // No grid slot: append only if not a hidden dup of a visible twin
            if (
              !isCfgVisible(columnConfig) &&
              reorderConfig.some((c) => c && sameKey(c, columnConfig) && isCfgVisible(c))
            ) {
              return;
            }
            // do not push if already present by key (covers any earlier write)
            if (!reorderConfig.some((c) => c && sameKey(c, columnConfig))) {
              reorderConfig.push(columnConfig);
              placedKeys.add(key);
            }
          }
        });

        // Columns that share a dataIndex (ex: "Stylecolor" and "Stylecolor ID") both resolve
        // to the same AG Grid slot during the passes above, so only one gets placed and the
        // other is silently dropped. Re-append any missing non-hideFromConfigurator columns
        // using the original pre-mutation config values so they dont inherit incorrect
        // pinning or visibility from sibling duplicate-dataIndex columns (ex: hiding 1 of the above cols that was pinned - was forcing the second to pin after apply)
        viewColumnsConfig
          .filter((c) => !c.hideFromConfigurator && !reorderConfig.some((r) => r && r.text === c.text))
          .forEach((c) => {
            const original = (raw as TenantConfigViewItem[])?.find((r) => r.text === c.text);
            reorderConfig.push(original ? { ...original } : c);
          });
      }
    }

    const baseConfig = reorderConfig.length > 0 ? reorderConfig.filter((x) => x) : reorderItems(viewColumnsConfig);
    // apply the manual grouping
    const groupedConfig = reorderItems(applyGroupingInPlace(baseConfig));

    this.setState({
      viewConfig: groupedConfig,
    });
  };

  onClose = async () => {
    this.setState({
      openOverride: undefined,
    });
  };

  onCancel = () => {
    this.onClose();
  };

  onSubmit = async () => {
    // Default any column lacking `visible` to true, unless `hideFromConfigurator` is set or missing property visible in option
    const grouped = this.state.viewConfig.map((col) =>
      col.hideFromConfigurator || 'visible' in col || 'hidden' in col ? col : { ...col, visible: true },
    );

    const flat = flattenConfigItems(grouped);

    // start with basic new config, not overrwriting view & columns every time
    const newConfig: TenantConfigViewData = {
      ...this.props.viewConfig,
      isDefault: false,
    };

    // if this view originally had a columns array at the root, update that & store grouped format separately
    if (Array.isArray(this.props.viewConfig.columns)) {
      newConfig.columns = flat;
      newConfig.keyGroupedColumns = grouped;
    }
    // view with view array
    if (Array.isArray(this.props.viewConfig.view)) {
      // preserve exact nested structure
      newConfig.view = grouped;
    }

    // if there’s a grid.view, keep it in sync as before
    if (this.props.viewConfig.grid) {
      newConfig.grid = {
        ...this.props.viewConfig.grid,
        view: grouped,
      };
    }

    if (this.props.updateConfig) {
      this.props.updateConfig(newConfig);
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(newConfig);
    }

    this.onClose();
  };

  onClickPinned = (item: TenantConfigViewItem) => {
    // during search, grouped items are copies not originals, so look up by dataIndex instead
    const index = this.state.viewConfig.findIndex((c) => c.dataIndex === item.dataIndex);
    item = this.state.viewConfig[index];
    if (!isPinned(item.pinned as string)) {
      item.pinned = 'left';
      if (item.columns) {
        item.columns = item.columns.map((x) => {
          return { ...x, pinned: 'left' };
        });
      }
    } else {
      item.pinned = undefined;
      if (item.columns) {
        item.columns = item.columns.map((x) => {
          return { ...x, pinned: undefined };
        });
      }
    }
    let updatedItems = clone(this.state.viewConfig); //.slice(0);
    updatedItems[index] = item;
    // updatedItems = reorderItems(updatedItems);
    // If it is newly pinned, add it to the front, if we removed pinning, move it to the first non pinned item
    // We don't need to reorder 1 or  fewer
    if (updatedItems.length > 2) {
      if (isPinned(item.pinned)) {
        updatedItems = without(updatedItems, item);
        updatedItems.unshift(item);
      } else {
        const firstItemNotPinnedIndex = updatedItems.findIndex((x) => item != x && !x.pinned) - 1;
        updatedItems = without(updatedItems, item);
        updatedItems.splice(firstItemNotPinnedIndex, 0, item);
      }
    }
    this.setState({
      viewConfig: updatedItems,
    });
  };

  onClickVisible = (item: TenantConfigViewItem) => {
    const { searchValue, viewConfig } = this.state;
    // Use dataIndex & text to find the original item in the full config, because during search the items are copies not originals
    const filteredItem = getFilteredViewConfig(searchValue, viewConfig).find(
      (configItem) => configItem.dataIndex === item.dataIndex && configItem.text === item.text,
    );

    if (!filteredItem) {
      return;
    }

    const targetVisible = !itemVisibleInFilter(filteredItem);
    const updatedItems = applyVisibilityToFilteredItems(this.state.viewConfig, [filteredItem], targetVisible);

    // Move to bottom only if the item ended up fully hidden after the update
    // For group items under a filter, unchecking only hides the filtered children —
    // other children may still be visible, so the group should stay in place
    const itemIndex = updatedItems.findIndex(
      (configItem) => configItem.dataIndex === filteredItem.dataIndex && configItem.text === filteredItem.text,
    );
    if (itemIndex > -1 && !itemVisible(updatedItems[itemIndex])) {
      updatedItems.push(updatedItems.splice(itemIndex, 1)[0]);
    }

    if (filteredItem.columns && filteredItem.columns.length > 0) {
      this.setState({
        viewConfig: updatedItems,
      });
      return;
    }

    this.setState({
      viewConfig: updatedItems,
    });
  };

  onChildrenClickVisible = (parentDataIndex: string, childDataIndex: string) => {
    const { searchValue, viewConfig } = this.state;
    const filteredParent = getFilteredViewConfig(searchValue, viewConfig).find(
      (item) => item.dataIndex === parentDataIndex,
    );
    const filteredChild = filteredParent?.columns?.find((child) => child.dataIndex === childDataIndex);

    if (!filteredParent || !filteredChild) {
      return;
    }

    const targetVisible = !itemVisible(filteredChild);

    this.setState(({ viewConfig }) => ({
      viewConfig: applyVisibilityToFilteredItems(
        viewConfig,
        [{ ...filteredParent, columns: [filteredChild] }],
        targetVisible,
      ),
    }));
  };

  onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    const { searchValue, viewConfig } = this.state;

    const reorder = (list: TenantConfigViewItem[], startIndex: number, endIndex: number): TenantConfigViewItem[] => {
      const updated = Array.from(list);
      const [removed] = updated.splice(startIndex, 1);
      updated.splice(endIndex, 0, removed);

      return updated;
    };

    const findIndexByKey = (items: TenantConfigViewItem[], key: string | undefined) => {
      return items.findIndex((item) => getConfigKey(item) === key);
    };

    // CHILD reordering
    if (type.startsWith('CHILD-')) {
      // extract the group's dataIndex
      const groupDataIndex = type.replace('CHILD-', '');
      // find that group in state
      const parentIndex = viewConfig.findIndex((item) => item.dataIndex === groupDataIndex);
      if (parentIndex < 0) return;

      const updated = cloneDeep(viewConfig);
      const children = updated[parentIndex].columns ?? [];
      const filteredParent = getFilteredViewConfig(searchValue, viewConfig).find(
        (item) => item.dataIndex === groupDataIndex,
      );
      const filteredChildren = filteredParent?.columns ?? children;
      const sourceIndex = findIndexByKey(children, getConfigKey(filteredChildren[source.index]));
      const destinationIndex = findIndexByKey(children, getConfigKey(filteredChildren[destination.index]));

      if (sourceIndex < 0 || destinationIndex < 0) {
        return;
      }

      updated[parentIndex].columns = reorder(children, sourceIndex, destinationIndex);
      return this.setState({ viewConfig: updated });
    }

    const filteredViewConfig = getFilteredViewConfig(searchValue, viewConfig);
    const sourceIndex = findIndexByKey(viewConfig, getConfigKey(filteredViewConfig[source.index]));
    const destinationIndex = findIndexByKey(viewConfig, getConfigKey(filteredViewConfig[destination.index]));

    if (sourceIndex < 0 || destinationIndex < 0) {
      return;
    }

    let newConfig = reorder(viewConfig, sourceIndex, destinationIndex) as TenantConfigViewItem[];
    newConfig = orderBy(newConfig, 'pinned', 'asc');
    newConfig = orderBy(
      newConfig.map((x) => {
        if (x.visible) {
          delete x.visible;
          delete x.hidden;
        }
        return x;
      }),
      'visible',
      'desc',
    );

    this.setState({ viewConfig: newConfig });
  };

  showResetConfirmation = () => {
    const modal = document.body.querySelector<HTMLDivElement>('[class^=MuiDialog]');
    modal ? (modal.style.zIndex = '51') : undefined;
    this.setState({
      showResetConfirmation: true,
    });
  };

  hideResetConfirmation = () => {
    this.setState({
      showResetConfirmation: false,
    });
  };

  resetViewConfigurator = () => {
    // TODO:Create better type for updateConfig handler

    const updatedViewConfig = { ...this.props.unmodifiedViewDefn, isDefault: true };
    if (this.props.updateConfig) {
      this.props.updateConfig(updatedViewConfig);
    }
    if (this.props.onSubmit) {
      this.props.onSubmit(updatedViewConfig as TenantConfigViewData);
    }

    this.setState({
      showResetConfirmation: false,
      viewConfig:
        (updatedViewConfig.view as TenantConfigViewItem[]) || (updatedViewConfig.columns as TenantConfigViewItem[]),
      openOverride: false,
    });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchValue: event.currentTarget.value });
  };

  handleClearSearch = () => {
    this.setState({ searchValue: '' });
  };

  render() {
    const { showPinCheckboxForGrid, subheaderTitle = '' } = this.props;
    const { showResetConfirmation, openOverride, searchValue, viewConfig } = this.state;
    const filteredViewConfig = getFilteredViewConfig(searchValue, viewConfig);
    const filteredLeafItems = getFilteredLeafItems(filteredViewConfig);
    const allFilteredVisible = filteredLeafItems.length > 0 && filteredLeafItems.every((item) => itemVisible(item));
    const modalTitle = !isEmpty(subheaderTitle) ? `Configure ${subheaderTitle} Metrics` : 'Configure Metrics';

    return (
      <MuiThemeProvider theme={muiTheme}>
        {/* theme provider re-exported here because for reasons that are unclear, the theme doesn't apply witin this component correctly */}
        <TemplateModalWithButton
          buttonComponent={
            // TODO: INT-2114: Fix this properly
            <SubheaderQuickActionButton
              iconClass={'fas fa-wrench'}
              style={{ opacity: 1, position: 'relative' }}
              tooltipText="Configure metrics in this view"
              buttonDataQa="subheader-wrench-button"
              ariaLabel="Open view configurator"
            />
          }
          title={modalTitle}
          onOpen={this.onOpen}
          onSubmit={this.onSubmit}
          onCancel={this.onCancel}
          showSubmit={true}
          openOverride={openOverride}
          styleOverrides={{ maxWidth: '550px' }} // define a max width only in this location, so title text wraps
          leftAction={
            <SquareActionButton
              dataQa="wrench-reset-btn"
              className={resetButtonStyles}
              onClick={this.showResetConfirmation}
              text="RESET"
              type="primary"
            />
          }
        >
          <ConfirmationModal
            isOpen={showResetConfirmation}
            descriptionText="Are you sure you want to reset?"
            onConfirm={() => this.resetViewConfigurator()}
            onCancel={() => this.hideResetConfirmation()}
          />
          <div className={viewConfiguratorStyle} data-qa="wrench-modal-container">
            <div className="columns-title-container">
              <p>Metric</p>
              {showPinCheckboxForGrid && <p>Pin</p>}
              <p>Show</p>
            </div>
            <div className="search-container">
              <OutlinedInput
                className="wrench-search"
                fullWidth={true}
                autoFocus
                inputProps={{
                  'data-qa': 'ViewConfiguratorSearchInput',
                  'aria-label': 'Search columns',
                }}
                endAdornment={
                  <IconButton size={'small'} onClick={this.handleClearSearch} aria-label="Clear search input">
                    <Close />
                  </IconButton>
                }
                value={searchValue}
                onChange={this.handleSearchChange}
                placeholder="Search columns... (= for exact)"
              />
              <div className="bulk-actions-container">
                <span>Select all</span>
                <label className="bulk-toggle">
                  <Checkbox
                    title="Toggle visibility on all"
                    inputProps={{
                      'aria-label': 'Toggle visibility on all',
                    }}
                    data-qa="toggle-all-checkbox"
                    checked={allFilteredVisible}
                    onChange={(e) => this.onSelectAllFiltered(e.target.checked)}
                  />
                </label>
              </div>
            </div>
            <div className="columns-grid">
              {filteredViewConfig && (
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="root" type="ROOT">
                    {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                    {(provided: any, snapshot: any) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver, filteredViewConfig.length)}
                      >
                        {filteredViewConfig.map((item: TenantConfigViewItem, index: number) => {
                          if (item.hideFromConfigurator) {
                            return;
                          }
                          const checkboxState = getParentCheckboxState(item, this.state.viewConfig);
                          const id = (item.dataIndex || item.text) + index;
                          const hasChildren = !isNil(item.columns);
                          return (
                            <Draggable key={id} draggableId={id} index={index}>
                              {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                              {(provided: any, snapshot: any) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                  data-qa="menu-option"
                                  aria-label={!hasChildren ? 'Option' : 'Parent option'}
                                >
                                  <StyledAccordion square={true}>
                                    <StyledAccordionSummary
                                      expandIcon={hasChildren ? <ExpandMore aria-label="Toggle option" /> : null}
                                      style={{
                                        backgroundColor: snapshot.isDragging ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.05)',
                                        cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                      }}
                                    >
                                      <StyledTypography variant="body2" noWrap={true} hasIcon={hasChildren}>
                                        <Tooltip title={item.text}>
                                          <span data-qa="view-config-metric-title">
                                            {item.viewConfiguratorName || item.text}
                                          </span>
                                        </Tooltip>
                                      </StyledTypography>

                                      <div className="checkbox-container">
                                        {showPinCheckboxForGrid && (
                                          <Checkbox
                                            title={'Pinned?'}
                                            inputProps={{ 'aria-label': 'Pinned?' }}
                                            checked={isPinned(item.pinned as string)}
                                            onClick={(e) => {
                                              // prevent the checkbox click from also toggling the accordion
                                              e.stopPropagation();
                                              this.onClickPinned(item);
                                            }}
                                          />
                                        )}
                                        <Checkbox
                                          title={'Visible?'}
                                          inputProps={{ 'aria-label': 'Visible?' }}
                                          checked={checkboxState.checked}
                                          indeterminate={checkboxState.indeterminate}
                                          onClick={(e) => {
                                            // prevent the checkbox click from also toggling the accordion
                                            e.stopPropagation();
                                            this.onClickVisible(item);
                                          }}
                                        />
                                      </div>
                                    </StyledAccordionSummary>
                                    <StyledAccordionDetails>
                                      {hasChildren && (
                                        <Droppable
                                          droppableId={`child-group-${item.dataIndex}`}
                                          type={`CHILD-${item.dataIndex}`}
                                        >
                                          {(childProv: any) => {
                                            return (
                                              <div ref={childProv.innerRef} {...childProv.droppableProps}>
                                                {item.columns?.map((col, idx) => (
                                                  <Draggable
                                                    key={`child-${item.dataIndex}-${col.dataIndex}`}
                                                    draggableId={`child-${item.dataIndex}-${col.dataIndex}`}
                                                    index={idx}
                                                  >
                                                    {(colProv, colSnapshot) => (
                                                      <div
                                                        ref={colProv.innerRef}
                                                        {...colProv.draggableProps}
                                                        {...colProv.dragHandleProps}
                                                        style={{
                                                          ...getItemStyle(
                                                            colSnapshot.isDragging,
                                                            colProv.draggableProps.style,
                                                          ),
                                                          display: 'grid',
                                                          marginLeft: 0,
                                                          marginRight: 0,
                                                          padding: '4px 0px 4px 8px',
                                                        }}
                                                        className="child-metric-container"
                                                        aria-label="Child option"
                                                      >
                                                        <StyledTypography hasIcon={hasChildren} noWrap variant="body2">
                                                          <Tooltip title={col.text}>
                                                            <span>{col.viewConfiguratorName || col.text}</span>
                                                          </Tooltip>
                                                        </StyledTypography>
                                                        <Checkbox
                                                          title={'Child Visible?'}
                                                          inputProps={{ 'aria-label': 'Child Visible?' }}
                                                          checked={itemVisible(col)}
                                                          onClick={() =>
                                                            this.onChildrenClickVisible(item.dataIndex, col.dataIndex)
                                                          }
                                                        />
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                ))}
                                                {childProv.placeholder}
                                              </div>
                                            );
                                          }}
                                        </Droppable>
                                      )}
                                    </StyledAccordionDetails>
                                  </StyledAccordion>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        </TemplateModalWithButton>
      </MuiThemeProvider>
    );
  }
}
